import { useCallback, useMemo, useState } from "react";
import { Button, Col, Input, notification, Row, Space, Spin, Table, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import locale from 'antd/es/locale/pt_BR';

import { IContaSaldoDeContas } from "../../../../../interfaces";
import { IUpdateConta } from "../../../AlinhamentoDeContasEFontes/interfaces";
import { IDescobertaCnpjContaItem, IDescobertaCnpjContaResponse, IGetContaById } from "../../interfaces";

import { EmitirErro } from "../../../../../../../../services/EmitirErro";
import { removeAcento } from "../../../../../../../../utils/RemoveAcentos";
import BotaoLimparFiltros, { checkHasActiveFilters } from "../../../../../../components/BotaoLimparFiltros";

function onlyNumbersAndLetters(value: string) {
    if (!value) {
        return "";
    }

    return value.replace(/[^a-zA-Z0-9]/g, '');
}

interface IProps {
    solicitacaoId: number;
    dadosDescoberta: IDescobertaCnpjContaResponse | null;
    setDadosDescoberta: React.Dispatch<React.SetStateAction<IDescobertaCnpjContaResponse | null>>;
    isLoading: boolean;
    onGetContaById: (data: IGetContaById) => Promise<IContaSaldoDeContas>;
    onUpdateConta: (data: IUpdateConta) => Promise<IContaSaldoDeContas | null>;
    onContaCorrigida?: () => void;
}

export const CorrigirCnpjsBB = ({
    solicitacaoId,
    dadosDescoberta,
    setDadosDescoberta,
    isLoading,
    onGetContaById,
    onUpdateConta,
    onContaCorrigida,
}: IProps) => {
    const [corrigindoContaId, setCorrigindoContaId] = useState<number | null>(null);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});

    const handleSearch = useCallback((selectedKeys: any[], confirm: () => void, dataIndex: string): void => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    }, []);

    const handleReset = useCallback((clearFilters: () => void): void => {
        clearFilters();
        setSearchText('');
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilteredInfo({});
        setSearchText('');
        setSearchedColumn('');
    }, []);

    const hasActiveFilters = useMemo(() => {
        return checkHasActiveFilters(filteredInfo, searchText);
    }, [filteredInfo, searchText]);

    const getColumnSearchProps = useCallback((
        dataIndex: string,
        valueFormatterBeforeCompare?: (value: string) => string,
    ) => {
        let searchInput: any = '';
        return ({
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
                <div style={{ padding: 8 }}>
                    <Input
                        ref={node => {
                            searchInput = node;
                        }}
                        placeholder={`Digite...`}
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Pesquisar
                        </Button>
                        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: any) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
            onFilter: (value: any, record: any) => {
                const recordValue = record[dataIndex]?.toString() || '';

                let trecord = removeAcento(valueFormatterBeforeCompare ? valueFormatterBeforeCompare(recordValue) : recordValue);
                const valueToCompare = removeAcento(valueFormatterBeforeCompare ? valueFormatterBeforeCompare(value) : value);

                return record[dataIndex] ? trecord.toLowerCase().includes(valueToCompare.toLowerCase()) : false;
            },
            onFilterDropdownVisibleChange: (visible: any) => {
                if (visible) {
                    setTimeout(() => searchInput?.select?.());
                }
            },
            render: (text: any) =>
                searchedColumn === dataIndex ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                ) : (
                    text
                ),
        });
    }, [searchText, searchedColumn, handleSearch, handleReset]);

    const handleCorrigir = useCallback(async (conta: IDescobertaCnpjContaItem) => {
        try {
            setCorrigindoContaId(conta.contaId);

            const contaCompleta = await onGetContaById({
                solicitacaoId,
                contaId: conta.contaId,
            });

            const fonteIds = contaCompleta?.fontesConta?.map((f: any) => f.fonteId || f.fonte?.id) || [];

            await onUpdateConta({
                solicitacaoId,
                contaId: conta.contaId,
                orgaoVinculadoId: conta.orgaoVinculadoDonoEncontradoId,
                descricao: contaCompleta.descricao,
                numero: contaCompleta.numero,
                agencia: contaCompleta.agencia,
                bancoId: contaCompleta.bancoId,
                fonteIds,
            });

            notification.success({
                message: 'Sucesso',
                description: `Ag ${conta.agencia} Conta ${conta.numero} corrigida com sucesso!`,
            });

            setDadosDescoberta((prev) => {
                if (!prev) return null;
                return {
                    ...prev,
                    contas: prev.contas.filter((c) => c.contaId !== conta.contaId),
                };
            });

            onContaCorrigida?.();
        } catch (error) {
            EmitirErro(error, true, 'corrigirCnpjsBB_corrigir_71289nasd98asd');
        } finally {
            setCorrigindoContaId(null);
        }
    }, [solicitacaoId, onGetContaById, onUpdateConta, setDadosDescoberta, onContaCorrigida]);

    const columns = [
        {
            title: 'Agência',
            dataIndex: 'agencia',
            key: 'agencia',
            filteredValue: filteredInfo.agencia || null,
            width: '12%',
            ...getColumnSearchProps('agencia', onlyNumbersAndLetters),
        },
        {
            title: 'Número',
            dataIndex: 'numero',
            key: 'numero',
            filteredValue: filteredInfo.numero || null,
            width: '15%',
            ...getColumnSearchProps('numero', onlyNumbersAndLetters),
        },
        {
            title: 'Órgão vinculado atual',
            dataIndex: 'nomeOrgaoVinculadoOriginal',
            key: 'nomeOrgaoVinculadoOriginal',
            filteredValue: filteredInfo.nomeOrgaoVinculadoOriginal || null,
            ...getColumnSearchProps('nomeOrgaoVinculadoOriginal'),
        },
        {
            title: 'Órgão vinculado encontrado',
            dataIndex: 'nomeOrgaoVinculadoDonoEncontrado',
            key: 'nomeOrgaoVinculadoDonoEncontrado',
            filteredValue: filteredInfo.nomeOrgaoVinculadoDonoEncontrado || null,
            ...getColumnSearchProps('nomeOrgaoVinculadoDonoEncontrado'),
        },
        {
            title: 'Ações',
            key: 'acoes',
            width: '10%',
            render: (_: any, record: IDescobertaCnpjContaItem) => (
                <Button
                    type="primary"
                    size="small"
                    loading={corrigindoContaId === record.contaId}
                    disabled={corrigindoContaId !== null && corrigindoContaId !== record.contaId}
                    onClick={() => handleCorrigir(record)}
                    style={{
                        borderRadius: 4,
                    }}
                >
                    Corrigir
                </Button>
            ),
        },
    ];

    return (
        <Spin spinning={isLoading}>
            {!isLoading && (!dadosDescoberta || dadosDescoberta.contas.length === 0) && (
                <Col span={24}>
                    <Typography.Paragraph>Não há contas com CNPJ incorreto</Typography.Paragraph>
                </Col>
            )}

            <Row gutter={[0, 12]} style={{ padding: '8px 0' }}>
                {dadosDescoberta && dadosDescoberta.contas.length > 0 && (
                    <Col span={24}>
                        <div style={{ marginBottom: 8 }}>
                            <BotaoLimparFiltros
                                hasActiveFilters={hasActiveFilters}
                                onClick={handleClearAllFilters}
                            />
                        </div>
                        <Table
                            rowKey="contaId"
                            size="small"
                            dataSource={dadosDescoberta.contas}
                            columns={columns}
                            pagination={{ locale: { items_per_page: '' } }}
                            locale={locale.Table}
                            onChange={(pagination, filters, sorter, extra) => {
                                setFilteredInfo(filters || {});
                            }}
                        />
                    </Col>
                )}
            </Row>
        </Spin>
    );
};
export default CorrigirCnpjsBB;
